import { InputAdornment } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { ChangeEvent, useCallback, useMemo, useState } from 'react';
import { PasswordStrengthOptions } from '../../utils/zxcvbn';
import { IconButton } from '../IconButton';
import { CloseIcon } from '../Icons/CloseIcon';
import { DollarSignIcon } from '../Icons/DollarSignIcon';
import { ExpandMoreIcon } from '../Icons/ExpandMoreIcon';
import { HelpOutlineIcon } from '../Icons/HelpOutlineIcon';
import { VisibilityIcon } from '../Icons/VisibilityIcon';
import { VisibilityOffIcon } from '../Icons/VisibilityOffIcon';
import { PasswordStrengthThermometer } from '../PasswordStrengthThermometer';
import { Tooltip } from '../Tooltip';
import { Typography } from '../Typography';
import { CurrencyField } from './CurrencyField';
import {
  MainColorIcon,
  StyledBoxWrapper,
  StyledChipWrapperBox,
  StyledDatePickerField,
  StyledField,
  StyledIconWrapper,
  StyledListSubheader,
  StyledListSubheaderBox,
  StyledMenuItem,
  StyledMenuItemContent,
  StyledSelectedOptionBox,
  StyledTooltipBox,
} from './Field.styles';
import { PhoneNumberField } from './PhoneNumberField';
import { FieldProps, FieldTypes, MenuItemType } from './types';
import { getMenuItem } from './utils';

export function Field({
  type,
  kind = 'light',
  menuItems,
  iconLeft,
  mb,
  mt,
  ml,
  mr,
  pt,
  pb,
  pl,
  pr,
  margin,
  padding,
  width,
  errorMsg,
  showPasswordThermometer = false,
  passwordStrength = PasswordStrengthOptions.Weakest,
  showRequirementTooltip = false,
  inputProps = {},
  handleDatePickerChange,
  clearable,
  showDescription = false,
  ...rest
}: FieldProps) {
  const [showPassword, setShowPassword] = useState(false);

  const handleClickShowPassword = useCallback(
    () => setShowPassword((prev) => !prev),
    [],
  );

  const commonProps = useMemo(
    () => ({
      error: Boolean(errorMsg),
      fullWidth: true,
      helperText: errorMsg,
      kind,
      sx: {
        marginBottom: mb,
        marginTop: mt,
        marginLeft: ml,
        marginRight: mr,
        paddingTop: pt,
        paddingBottom: pb,
        paddingLeft: pl,
        paddingRight: pr,
        margin,
        padding,
        width,
      },
      ...rest,
    }),
    [
      errorMsg,
      kind,
      mb,
      mt,
      ml,
      mr,
      pt,
      pb,
      pl,
      pr,
      margin,
      padding,
      width,
      rest,
    ],
  );

  const PasswordField = useMemo(
    () => (
      <>
        <StyledField
          {...commonProps}
          id={`password-${rest.label}`}
          label={rest.label}
          margin="normal"
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <MainColorIcon
                    aria-label="Toggle visibility"
                    edge="end"
                    onClick={handleClickShowPassword}
                  >
                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </MainColorIcon>
                </InputAdornment>
              ),
              ...inputProps,
            },
          }}
          type={showPassword ? 'text' : 'password'}
        />
        {showPasswordThermometer ? (
          <PasswordStrengthThermometer
            showRequirementTooltip={showRequirementTooltip}
            strength={passwordStrength}
          />
        ) : null}
      </>
    ),
    [
      commonProps,
      handleClickShowPassword,
      passwordStrength,
      rest.label,
      showPassword,
      showPasswordThermometer,
      showRequirementTooltip,
      inputProps,
    ],
  );

  const TextField = useMemo(
    () => (
      <StyledField
        {...commonProps}
        slotProps={{
          input: {
            startAdornment: iconLeft && (
              <StyledIconWrapper>{iconLeft}</StyledIconWrapper>
            ),
            endAdornment: clearable && (
              <IconButton
                aria-label="clear input"
                onClick={() =>
                  rest.onChange({
                    target: { value: '' },
                  } as ChangeEvent<HTMLInputElement>)
                }
              >
                <CloseIcon />
              </IconButton>
            ),
            ...inputProps,
          },
        }}
      />
    ),
    [clearable, commonProps, iconLeft, inputProps, rest],
  );

  const CurrencyComponentField = useMemo(
    () => (
      <CurrencyField
        {...commonProps}
        slotProps={{
          input: {
            startAdornment: (
              <StyledIconWrapper>
                {iconLeft || <DollarSignIcon />}
              </StyledIconWrapper>
            ),
            ...inputProps,
          },
        }}
      />
    ),
    [commonProps, iconLeft, inputProps],
  );

  const SelectField = useMemo(
    () => (
      <StyledField
        {...commonProps}
        select
        slotProps={{
          select: {
            renderValue: (selected) => {
              const selectedItem = menuItems?.find(
                (item) => item.key === selected,
              );
              if (!selectedItem) return '';
              return (
                <StyledMenuItemContent>
                  <StyledBoxWrapper>
                    {selectedItem.leftIcon ? (
                      <StyledIconWrapper side="left">
                        {selectedItem.leftIcon}
                      </StyledIconWrapper>
                    ) : null}
                    <StyledSelectedOptionBox>
                      {getMenuItem(selectedItem, showDescription)}
                    </StyledSelectedOptionBox>
                  </StyledBoxWrapper>
                  {selectedItem.rightChip ? (
                    <StyledChipWrapperBox>
                      <StyledIconWrapper side="right">
                        {selectedItem.rightChip}
                      </StyledIconWrapper>
                    </StyledChipWrapperBox>
                  ) : null}
                </StyledMenuItemContent>
              );
            },
            IconComponent: ExpandMoreIcon,
          },
          input: {
            ...inputProps,
          },
        }}
      >
        {menuItems?.map((menuItem) => {
          const {
            key,
            leftIcon,
            rightChip,
            itemType = MenuItemType.option,
            groupLabel,
            groupTooltip,
            tooltipMessage,
            tooltipIcon,
            groupChip,
          } = menuItem;
          return itemType === MenuItemType.option ? (
            <StyledMenuItem value={key}>
              <StyledBoxWrapper>
                {leftIcon ? (
                  <StyledIconWrapper side="left">{leftIcon}</StyledIconWrapper>
                ) : null}
                <StyledMenuItemContent>
                  {getMenuItem(menuItem, showDescription)}
                </StyledMenuItemContent>
              </StyledBoxWrapper>
              <StyledChipWrapperBox isMenuItem>
                {rightChip ? (
                  <StyledIconWrapper side="right">
                    {rightChip}
                  </StyledIconWrapper>
                ) : null}
              </StyledChipWrapperBox>
            </StyledMenuItem>
          ) : (
            <StyledListSubheader>
              <StyledListSubheaderBox>
                <Typography variant="subtitle">{groupLabel}</Typography>
                {groupChip ? groupChip : null}
              </StyledListSubheaderBox>
              <StyledTooltipBox>
                {groupTooltip ? (
                  <Tooltip placement="bottom-end" title={tooltipMessage}>
                    <IconButton aria-label="Tooltip button">
                      {tooltipIcon || <HelpOutlineIcon />}
                    </IconButton>
                  </Tooltip>
                ) : null}
              </StyledTooltipBox>
            </StyledListSubheader>
          );
        })}
      </StyledField>
    ),
    [commonProps, inputProps, menuItems, showDescription],
  );

  const PhoneField = useMemo(
    () => <PhoneNumberField {...commonProps} />,
    [commonProps],
  );

  const DateField = useMemo(
    () => (
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <StyledDatePickerField
          disabled={rest.disabled}
          label={rest.label}
          onChange={handleDatePickerChange}
          value={dayjs(rest.value)}
        />
      </LocalizationProvider>
    ),
    [handleDatePickerChange, rest.disabled, rest.label, rest.value],
  );

  switch (type) {
    case FieldTypes.password:
      return PasswordField;
    case FieldTypes.text:
      return TextField;
    case FieldTypes.currency:
      return CurrencyComponentField;
    case FieldTypes.select:
      return SelectField;
    case FieldTypes.phone:
      return PhoneField;
    case FieldTypes.date:
      return DateField;
    default:
      throw new Error('Invalid prop value for `type`.');
  }
}
